import TransactionBadge from 'admin/components/shared/TransactionBadge';
import { COLOR } from 'admin/constants/styles';
import { formatDate, formatDeviceModel, formatNum, formatUUID } from 'admin/utils';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Table from '../../../atoms/Table';

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
`;

const AmountList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;

  li {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #ddd;
    padding: 4px 0;

    &:first-child {
      justify-content: center;
      border-top: none;
    }
  }
`;

const Badge = styled(TransactionBadge)`
  border-radius: 6px;
`;

const BadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 6px;
`;

/**
 * 取引種別の隣に出す併用払いの印。取引種別のバッジ色は種別ごとに使い切っているので、
 * どの種別とも被らないコーポレートカラーを白抜きで使う。
 * このプロジェクトは bootstrap の _badge.scss を読み込んでいないため、取引種別の
 * バッジは font-size / weight / line-height を継承したまま padding だけ効いている。
 * 高さを揃えるにはこちらも同じく継承させ、枠線は箱を太らせない inset shadow で描く
 */
const CombinedPaymentBadge = styled.span`
  display: inline-block;
  padding: 0.35em 0.6em;
  text-align: center;
  border-radius: 6px;
  background-color: white;
  color: ${COLOR.corporatePrimaryColor};
  box-shadow: inset 0 0 0 1px ${COLOR.corporatePrimaryColor};
  white-space: nowrap;
`;

const createHeaders = (canReadPersonalInfo) => [
  { label: '取引種別', size: 1, align: 'left' },
  { label: '最終更新日時', size: 2, align: 'left' },
  { label: canReadPersonalInfo ? 'お客様ニックネーム' : 'お客様ID', size: 1.5, align: 'left' },
  {
    label: '店舗名',
    size: 2,
    align: 'left',
  },
  {
    label: '端末名',
    size: 2,
    align: 'left',
  },
  { label: '総額', size: 3.5, align: 'center' },
];

const DetailTable = ({ transaction, user, privateMoneyId }) => {
  const {
    done_at,
    shop_name,
    shop_id,
    user_id,
    customer_name,
    terminal_id,
    money_amount,
    raw_point_amount,
    campaign_point_amount,
    device_id,
    device_serial_number,
    device_type,
  } = transaction;

  const headers = createHeaders(user.isAllowedTo('read', 'personalInfo'));

  const deviceLink = (device_type, device_serial_number) => {
    if (device_serial_number) {
      if (device_type.slice(0, 2) === 'PD') {
        const str = formatDeviceModel(device_type);
        return (
          <Link to={`/settings/devices/pokeregi/${device_serial_number}`}>
            <br />
            {str}
          </Link>
        );
      }

      switch (device_type) {
        case 'payregi': {
          return (
            <Link to={`/settings/devices/payregi/${device_id}`}>
              ペイレジ
              <br />
              {device_serial_number}
            </Link>
          );
        }
        case 'paybox': {
          return (
            <Link to={`/settings/devices/paybox/${device_id}`}>
              ペイボックス
              <br />
              {device_serial_number}
            </Link>
          );
        }
        case 'payspot': {
          return (
            <Link to={`/settings/devices/payspot/${device_id}`}>
              ペイスポット
              <br />
              {device_serial_number}
            </Link>
          );
        }
      }
    }

    return null;
  };

  return (
    <Table styles={{ marginTop: '40px' }}>
      <Table.Header>
        <Table.Row noBorder styles={{ backgroundColor: '#eee' }}>
          {headers.map((c) => (
            <Table.Cell size={c.size} key={c.label} align={c.align} styles={{ fontWeight: 'bold' }}>
              {c.label}
            </Table.Cell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <Table.Row noBorder>
          <Table.Cell wordBreak="keep-all">
            <BadgeStack>
              <Badge transaction={transaction} />
              {transaction.combined_payment && (
                <CombinedPaymentBadge>併用払い</CombinedPaymentBadge>
              )}
            </BadgeStack>
          </Table.Cell>
          <Table.Cell>{formatDate(done_at)}</Table.Cell>
          {user.isAllowedTo('read', 'personalInfo') ? (
            <Table.Cell>
              <DetailWrapper>
                <br />
                {customer_name}
                <br />
                <Link to={`/customers/${user_id}?private_money_id=${privateMoneyId}`}>
                  {`(${formatUUID(user_id)})`}
                </Link>
              </DetailWrapper>
            </Table.Cell>
          ) : (
            <Table.Cell>
              <DetailWrapper>
                <br />
                <br />
                {`(${formatUUID(user_id)})`}
              </DetailWrapper>
            </Table.Cell>
          )}
          <Table.Cell wordBreak="break-word">
            <DetailWrapper>
              <br />
              {shop_name}
              <br />
              {`(${formatUUID(shop_id)})`}
            </DetailWrapper>
          </Table.Cell>
          <Table.Cell>
            <DetailWrapper>
              <div>{deviceLink(device_type, device_serial_number)}</div>
              <div>{!!terminal_id && `(${formatUUID(terminal_id)})`}</div>
            </DetailWrapper>
          </Table.Cell>
          <Table.Cell>
            <AmountList>
              <li>¥{formatNum(money_amount + raw_point_amount)}</li>
              <li>
                <span>マネー</span>
                <span>¥{formatNum(money_amount)}</span>
              </li>
              <li>
                <span>ポイント</span>
                <span>¥{formatNum(raw_point_amount)}</span>
              </li>
              <li>
                <span>ポイント（キャンペーン付与分）</span>
                <span>¥{formatNum(campaign_point_amount)}</span>
              </li>
            </AmountList>
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
};

export default DetailTable;
